import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'alinea:user-preferences';

export type ThemePreference = 'light' | 'dark' | 'system';
export type DateFormatPreference = 'short' | 'medium' | 'long';

export interface UserPreferences {
  /** Language for UI translations (e.g., 'pt-BR', 'en-US') */
  language: string;
  /** Locale for date/number formatting (e.g., 'pt-BR', 'en-US') */
  locale: string;
  /** Theme preference */
  theme: ThemePreference;
  /** Date display format preference */
  dateFormat: DateFormatPreference;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  language: 'pt-BR',
  locale: 'pt-BR',
  theme: 'system',
  dateFormat: 'short',
};

/**
 * Get browser/OS locale as fallback.
 */
function getSystemLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'pt-BR';
}

/**
 * Get system theme preference.
 */
export function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/** Resolve stored theme preference to the color mode applied on `<html>`. */
export function resolveColorModeFromThemePreference(theme: ThemePreference): 'light' | 'dark' {
  if (theme === 'light' || theme === 'dark') {
    return theme;
  }
  return getSystemTheme();
}

/**
 * Load preferences from localStorage, falling back to system/defaults.
 */
export function loadUserPreferences(): UserPreferences {
  if (typeof localStorage === 'undefined') {
    return {
      ...DEFAULT_PREFERENCES,
      language: getSystemLocale(),
    };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<UserPreferences>;
      return {
        language: parsed.language ?? getSystemLocale(),
        locale: parsed.locale ?? DEFAULT_PREFERENCES.locale,
        theme: parsed.theme ?? 'system',
        dateFormat: parsed.dateFormat ?? 'short',
      };
    }
  } catch {
    // Invalid JSON, ignore
  }

  return {
    ...DEFAULT_PREFERENCES,
    language: getSystemLocale(),
  };
}

/**
 * Save preferences to localStorage.
 */
function savePreferences(prefs: UserPreferences): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage full or unavailable
  }
}

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  effectiveTheme: 'light' | 'dark';
  setPreferences: (updates: Partial<UserPreferences>) => void;
  resetToSystem: () => void;
  setLanguage: (language: string) => void;
  setLocale: (locale: string) => void;
  setTheme: (theme: ThemePreference) => void;
  setDateFormat: (dateFormat: DateFormatPreference) => void;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

/** Shared user preferences state for the entire app tree. */
export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferencesState] = useState<UserPreferences>(loadUserPreferences);

  useEffect(() => {
    setPreferencesState(loadUserPreferences());
  }, []);

  const setPreferences = useCallback((updates: Partial<UserPreferences>) => {
    setPreferencesState((prev) => {
      const next = { ...prev, ...updates };
      savePreferences(next);
      return next;
    });
  }, []);

  const resetToSystem = useCallback(() => {
    const systemPrefs: UserPreferences = {
      language: getSystemLocale(),
      locale: getSystemLocale(),
      theme: 'system',
      dateFormat: 'short',
    };
    savePreferences(systemPrefs);
    setPreferencesState(systemPrefs);
  }, []);

  const effectiveTheme = useMemo(() => {
    if (preferences.theme === 'system') {
      return getSystemTheme();
    }
    return preferences.theme;
  }, [preferences.theme]);

  const setLanguage = useCallback(
    (language: string) => setPreferences({ language }),
    [setPreferences],
  );
  const setLocale = useCallback((locale: string) => setPreferences({ locale }), [setPreferences]);
  const setTheme = useCallback(
    (theme: ThemePreference) => setPreferences({ theme }),
    [setPreferences],
  );
  const setDateFormat = useCallback(
    (dateFormat: DateFormatPreference) => setPreferences({ dateFormat }),
    [setPreferences],
  );

  const value = useMemo(
    () => ({
      preferences,
      effectiveTheme,
      setPreferences,
      resetToSystem,
      setLanguage,
      setLocale,
      setTheme,
      setDateFormat,
    }),
    [
      preferences,
      effectiveTheme,
      setPreferences,
      resetToSystem,
      setLanguage,
      setLocale,
      setTheme,
      setDateFormat,
    ],
  );

  return createElement(UserPreferencesContext.Provider, { value }, children);
}

/**
 * Hook to manage user preferences with localStorage persistence.
 * Defaults to browser/OS settings, allows user overrides.
 */
export function useUserPreferences(): UserPreferencesContextValue {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}

// ============================================================================
// Utility functions for use outside React components
// ============================================================================

/**
 * Locale the user explicitly picked in settings, if any — `null` when they haven't
 * (as opposed to {@link getUserLocale}, which falls back to the browser's locale).
 * Safe to call outside React components.
 */
export function getExplicitUserLocale(): string | null {
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<UserPreferences>;
        if (parsed.locale) return parsed.locale;
      }
    } catch {
      // Ignore
    }
  }
  return null;
}

/**
 * Get user's preferred locale (checks localStorage first, then browser).
 * Safe to call outside React components.
 */
export function getUserLocale(): string {
  return getExplicitUserLocale() ?? DEFAULT_PREFERENCES.locale;
}

/**
 * Get user's preferred language (checks localStorage first, then browser).
 * Safe to call outside React components.
 */
export function getUserLanguage(): string {
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<UserPreferences>;
        if (parsed.language) return parsed.language;
      }
    } catch {
      // Ignore
    }
  }
  return getSystemLocale();
}

/**
 * Get user's preferred date format style.
 * Safe to call outside React components.
 */
export function getUserDateFormat(): DateFormatPreference {
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<UserPreferences>;
        if (parsed.dateFormat) return parsed.dateFormat;
      }
    } catch {
      // Ignore
    }
  }
  return 'short';
}
